/**
 * Player Multi Selector Component
 * Componente Lit per selezione multipla di giocatori da aggiungere a un torneo
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface Player {
    id: number;
    display_name: string;
    skill_level: string;
    skill_level_display: string;
    first_name?: string;
    last_name?: string;
    archived?: boolean;
    gender?: string | undefined;  // 'M', 'F', 'O' or undefined
}

@customElement('pm-player-multi-selector')
export class PmPlayerMultiSelector extends LitElement {
    @property({ type: String, attribute: 'tournament-id' }) tournamentId: string = '0';
    @property({ type: Array }) availablePlayers: Player[] = [];
    @property({ type: Number, attribute: 'max-selectable' }) maxSelectable: number = 0;
    @property({ type: String, attribute: 'select-element-id' }) selectElementId: string = '';
    
    @state() private selectedPlayers: Set<number> = new Set();
    @state() private searchTerm: string = '';
    @state() private filterSkill: string = 'all';
    @state() private filterGender: string = 'all';
    @state() private isLoading: boolean = false;
    @state() private errorMessage: string = '';
    @state() private successMessage: string = '';

    static styles = css`
        :host {
            display: block;
        }

        .modal-header {
            border-bottom: 1px solid #dee2e6;
            padding: 1rem 1.5rem;
        }

        .modal-title {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 500;
            color: #212529;
        }

        .modal-body {
            padding: 1.5rem;
            max-height: 600px;
            overflow-y: auto;
        }

        .modal-footer {
            border-top: 1px solid #dee2e6;
            padding: 1rem 1.5rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        /* Filters */
        .filters-section {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1.5rem;
        }

        .filter-row {
            display: flex;
            gap: 1rem;
            align-items: center;
        }

        .filter-group {
            flex: 1;
        }

        .filter-group label {
            display: block;
            font-size: 0.875rem;
            font-weight: 500;
            margin-bottom: 0.25rem;
            color: #495057;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 0.875rem;
        }

        .search-input:focus {
            outline: none;
            border-color: #0d6efd;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .skill-select {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 0.875rem;
        }

        /* Stats Bar */
        .stats-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #e7f1ff;
            border-left: 4px solid #0d6efd;
            padding: 0.75rem 1rem;
            border-radius: 4px;
            margin-bottom: 1rem;
        }

        .stats-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .stats-label {
            font-size: 0.875rem;
            color: #495057;
        }

        .stats-value {
            font-size: 1rem;
            font-weight: 600;
            color: #0d6efd;
        }

        /* Player List */
        .players-list {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        .player-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.75rem;
            border: 1px solid #dee2e6;
            border-radius: 6px;
            transition: all 0.2s;
            cursor: pointer;
        }

        .player-item:hover {
            background: #f8f9fa;
            border-color: #adb5bd;
        }

        .player-item.selected {
            background: #e7f1ff;
            border-color: #0d6efd;
        }

        .player-checkbox {
            width: 20px;
            height: 20px;
            cursor: pointer;
        }

        .player-info {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .player-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 600;
            font-size: 1rem;
        }

        .player-details {
            flex: 1;
        }

        .player-name {
            font-weight: 500;
            color: #212529;
            margin-bottom: 0.25rem;
        }

        .player-meta {
            font-size: 0.875rem;
            color: #6c757d;
        }

        .skill-badge {
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 500;
            white-space: nowrap;
        }

        .skill-principiante {
            background: #d1ecf1;
            color: #0c5460;
        }

        .skill-intermedio {
            background: #d4edda;
            color: #155724;
        }

        .skill-avanzato {
            background: #fff3cd;
            color: #856404;
        }

        .skill-esperto {
            background: #f8d7da;
            color: #721c24;
        }

        .skill-professionale {
            background: #d6d8db;
            color: #383d41;
        }

        /* Empty State */
        .empty-state {
            text-align: center;
            padding: 3rem 1rem;
            color: #6c757d;
        }

        .empty-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
            opacity: 0.5;
        }

        /* Alert Messages */
        .alert {
            padding: 0.75rem 1rem;
            border-radius: 6px;
            margin-bottom: 1rem;
        }

        .alert-danger {
            background: #f8d7da;
            color: #721c24;
            border: 1px solid #f5c6cb;
        }

        .alert-success {
            background: #d4edda;
            color: #155724;
            border: 1px solid #c3e6cb;
        }

        .alert-warning {
            background: #fff3cd;
            color: #856404;
            border: 1px solid #ffeeba;
        }

        /* Buttons */
        .btn {
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
        }

        .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .btn-secondary {
            background: #6c757d;
            color: white;
        }

        .btn-secondary:hover:not(:disabled) {
            background: #5a6268;
        }

        .btn-primary {
            background: #0d6efd;
            color: white;
        }

        .btn-primary:hover:not(:disabled) {
            background: #0b5ed7;
        }

        .btn-sm {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
        }

        /* Loading Spinner */
        .spinner {
            display: inline-block;
            width: 1rem;
            height: 1rem;
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
            to { transform: rotate(360deg); }
        }

        /* Actions */
        .bulk-actions {
            display: flex;
            gap: 0.5rem;
        }

        .footer-info {
            font-size: 0.875rem;
            color: #6c757d;
        }

        .footer-info strong {
            color: #212529;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        // Carica giocatori disponibili dal DOM se non passati come prop
        this.loadPlayersFromDOM();
    }

    private loadPlayersFromDOM() {
        if (this.availablePlayers.length > 0) return;

        // Cerca nel DOM i giocatori disponibili usando l'ID specificato
        const selectElement = this.selectElementId 
            ? document.getElementById(this.selectElementId)
            : document.querySelector('select[name="player_id"]');
            
        if (selectElement) {
            const options = Array.from(selectElement.querySelectorAll('option')).filter(
                opt => opt.value && opt.value !== ''
            );

            this.availablePlayers = options.map(opt => {
                // Usa data-attributes se disponibili, altrimenti fa parsing del testo
                const dataName = opt.getAttribute('data-name');
                const dataSkill = opt.getAttribute('data-skill');
                const dataGender = opt.getAttribute('data-gender');
                
                if (dataName && dataSkill) {
                    return {
                        id: parseInt(opt.value),
                        display_name: dataName,
                        skill_level: dataSkill,
                        skill_level_display: opt.textContent?.match(/\((.+?)\)$/)?.[1] || dataSkill,
                        gender: dataGender || undefined
                    };
                } else {
                    // Fallback: parsing del testo
                    const text = opt.textContent || '';
                    const match = text.match(/^(.+?)\s*\((.+?)\)$/);
                    const displayName = match ? match[1].trim() : text.trim();
                    const skillDisplay = match ? match[2].trim() : 'Intermedio';

                    return {
                        id: parseInt(opt.value),
                        display_name: displayName,
                        skill_level: skillDisplay.toLowerCase(),
                        skill_level_display: skillDisplay,
                        gender: dataGender || undefined
                    };
                }
            });
        }
    }

    private getFilteredPlayers(): Player[] {
        return this.availablePlayers.filter(player => {
            // Filtro ricerca
            const matchesSearch = !this.searchTerm || 
                player.display_name.toLowerCase().includes(this.searchTerm.toLowerCase());

            // Filtro skill level
            const matchesSkill = this.filterSkill === 'all' || 
                player.skill_level === this.filterSkill;

            // Filtro genere
            const matchesGender = this.filterGender === 'all' || 
                player.gender === this.filterGender;

            return matchesSearch && matchesSkill && matchesGender;
        });
    }

    private togglePlayer(playerId: number) {
        if (this.selectedPlayers.has(playerId)) {
            this.selectedPlayers.delete(playerId);
        } else {
            if (this.maxSelectable > 0 && this.selectedPlayers.size >= this.maxSelectable) {
                this.errorMessage = `Puoi selezionare massimo ${this.maxSelectable} giocatori`;
                setTimeout(() => this.errorMessage = '', 3000);
                return;
            }
            this.selectedPlayers.add(playerId);
        }
        this.requestUpdate();
    }

    private selectAll() {
        const filtered = this.getFilteredPlayers();
        const toAdd = this.maxSelectable > 0 
            ? filtered.slice(0, this.maxSelectable - this.selectedPlayers.size)
            : filtered;
        
        toAdd.forEach(player => this.selectedPlayers.add(player.id));
        this.requestUpdate();
    }

    private deselectAll() {
        this.selectedPlayers.clear();
        this.requestUpdate();
    }

    private async handleSubmit() {
        if (this.selectedPlayers.size === 0) {
            this.errorMessage = 'Seleziona almeno un giocatore';
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';

        try {
            const csrfToken = this.getCsrfToken();
            const playerIds = Array.from(this.selectedPlayers);

            const response = await fetch(
                `/simulations/tournaments/${this.tournamentId}/participants/bulk-add-existing-players/`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ player_ids: playerIds }),
                }
            );

            if (response.ok) {
                this.successMessage = `${playerIds.length} giocatori aggiunti con successo!`;
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                const data = await response.json();
                this.errorMessage = data.error || 'Errore durante l\'aggiunta dei giocatori';
            }
        } catch (error) {
            console.error('Errore:', error);
            this.errorMessage = 'Errore di connessione. Riprova.';
        } finally {
            this.isLoading = false;
        }
    }

    private getCsrfToken(): string {
        const tokenElement = document.querySelector('[name=csrfmiddlewaretoken]');
        return tokenElement ? (tokenElement as HTMLInputElement).value : '';
    }

    private getInitials(name: string): string {
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    }

    render() {
        const filteredPlayers = this.getFilteredPlayers();
        const selectedCount = this.selectedPlayers.size;
        const canSelectMore = this.maxSelectable === 0 || selectedCount < this.maxSelectable;

        return html`
            <div class="modal-header">
                <h5 class="modal-title">
                    <i class="fas fa-users-cog"></i> Selezione Multipla Giocatori
                </h5>
            </div>

            <div class="modal-body">
                ${this.errorMessage ? html`
                    <div class="alert alert-danger">
                        <i class="fas fa-exclamation-triangle"></i> ${this.errorMessage}
                    </div>
                ` : ''}

                ${this.successMessage ? html`
                    <div class="alert alert-success">
                        <i class="fas fa-check-circle"></i> ${this.successMessage}
                    </div>
                ` : ''}

                <!-- Filters -->
                <div class="filters-section">
                    <div class="filter-row">
                        <div class="filter-group">
                            <label>Cerca giocatore</label>
                            <input 
                                type="text" 
                                class="search-input"
                                placeholder="Nome o nickname..."
                                .value=${this.searchTerm}
                                @input=${(e: Event) => {
                                    this.searchTerm = (e.target as HTMLInputElement).value;
                                }}
                            />
                        </div>
                        <div class="filter-group">
                            <label>Livello abilità</label>
                            <select 
                                class="skill-select"
                                .value=${this.filterSkill}
                                @change=${(e: Event) => {
                                    this.filterSkill = (e.target as HTMLSelectElement).value;
                                }}
                            >
                                <option value="all">Tutti i livelli</option>
                                <option value="principiante">Principiante</option>
                                <option value="intermedio">Intermedio</option>
                                <option value="avanzato">Avanzato</option>
                                <option value="esperto">Esperto</option>
                                <option value="professionale">Professionale</option>
                            </select>
                        </div>
                        <div class="filter-group">
                            <label><i class="fas fa-venus-mars"></i> Genere</label>
                            <select 
                                class="skill-select"
                                .value=${this.filterGender}
                                @change=${(e: Event) => {
                                    this.filterGender = (e.target as HTMLSelectElement).value;
                                }}
                            >
                                <option value="all">Tutti</option>
                                <option value="M">♂️ Solo Maschi</option>
                                <option value="F">♀️ Solo Femmine</option>
                                <option value="O">⚧ Altro</option>
                            </select>
                        </div>
                    </div>
                </div>

                <!-- Stats Bar -->
                <div class="stats-bar">
                    <div class="stats-item">
                        <span class="stats-label">Selezionati:</span>
                        <span class="stats-value">${selectedCount}</span>
                        ${this.maxSelectable > 0 ? html`
                            <span class="stats-label">/ ${this.maxSelectable}</span>
                        ` : ''}
                    </div>
                    <div class="bulk-actions">
                        <button 
                            class="btn btn-sm btn-secondary"
                            @click=${this.selectAll}
                            ?disabled=${!canSelectMore || filteredPlayers.length === 0}
                        >
                            <i class="fas fa-check-square"></i> Seleziona Tutti
                        </button>
                        <button 
                            class="btn btn-sm btn-secondary"
                            @click=${this.deselectAll}
                            ?disabled=${selectedCount === 0}
                        >
                            <i class="fas fa-times-circle"></i> Deseleziona Tutti
                        </button>
                    </div>
                </div>

                <!-- Players List -->
                ${filteredPlayers.length === 0 ? html`
                    <div class="empty-state">
                        <div class="empty-icon">👥</div>
                        <h6>Nessun giocatore trovato</h6>
                        <p>Prova a modificare i filtri di ricerca</p>
                    </div>
                ` : html`
                    <div class="players-list">
                        ${filteredPlayers.map(player => {
                            const isSelected = this.selectedPlayers.has(player.id);
                            return html`
                                <div 
                                    class="player-item ${isSelected ? 'selected' : ''}"
                                    @click=${() => this.togglePlayer(player.id)}
                                >
                                    <input 
                                        type="checkbox" 
                                        class="player-checkbox"
                                        .checked=${isSelected}
                                        @click=${(e: Event) => e.stopPropagation()}
                                        @change=${() => this.togglePlayer(player.id)}
                                    />
                                    <div class="player-info">
                                        <div class="player-avatar">
                                            ${this.getInitials(player.display_name)}
                                        </div>
                                        <div class="player-details">
                                            <div class="player-name">
                                                ${player.display_name}
                                                ${player.gender === 'M' ? html`<i class="fas fa-mars" style="color: #2196F3; margin-left: 0.25rem;" title="Maschile"></i>` : ''}
                                                ${player.gender === 'F' ? html`<i class="fas fa-venus" style="color: #E91E63; margin-left: 0.25rem;" title="Femminile"></i>` : ''}
                                                ${player.gender === 'O' ? html`<i class="fas fa-venus-mars" style="color: #9e9e9e; margin-left: 0.25rem;" title="Altro"></i>` : ''}
                                            </div>
                                            <div class="player-meta">
                                                <span class="skill-badge skill-${player.skill_level}">
                                                    ${player.skill_level_display}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `;
                        })}
                    </div>
                `}
            </div>

            <div class="modal-footer">
                <div class="footer-info">
                    <strong>${filteredPlayers.length}</strong> giocatori disponibili
                </div>
                <div style="display: flex; gap: 0.5rem;">
                    <button 
                        class="btn btn-secondary"
                        @click=${() => this.dispatchEvent(new CustomEvent('close'))}
                        ?disabled=${this.isLoading}
                    >
                        <i class="fas fa-times"></i> Annulla
                    </button>
                    <button 
                        class="btn btn-primary"
                        @click=${this.handleSubmit}
                        ?disabled=${this.isLoading || selectedCount === 0}
                    >
                        ${this.isLoading ? html`
                            <span class="spinner"></span> Aggiunta in corso...
                        ` : html`
                            <i class="fas fa-plus"></i> Aggiungi ${selectedCount} Giocatori
                        `}
                    </button>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pm-player-multi-selector': PmPlayerMultiSelector;
    }
}
